import React, { useCallback, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { getTranslatedText, Loader, useIntl } from '@nile/components';
import InlineLoader from './InlineLoader';
import { isEmpty } from 'lodash';
import { SVGIcon } from '@superops/super-icons';
import { Button, Link, Sheet, TextStyle } from '@superops/ui-design-system';
import AICopyAndPaste from '../AICopyAndPaste';
import {
  dompurifier,
  EventEmitterWithIndex,
  useActivityTracker
} from '@nile/components/src/Utils/helpers';
import {
  AccountClient,
  GET_AI_RECOMMENDED_SOLUTION_REPLY_V2,
  MLClient
} from '@nile/graphql';
import markdownit from 'markdown-it';
import { FormattedMessage } from 'react-intl';
import { GENERATE_SHARE_LINK } from '@nile/rmm/src/modules/knowledge-base/graphql';
import ReactMarkdown from 'react-markdown';
import ArticleReader from '@nile/rmm/src/modules/knowledge-base/components/ArticleSearchSheet/ArticleReader';

interface TicketData {
  ticketId: string;
  displayId: string;
  subject: string;
  siteName: string;
}

interface RecommendedSolution {
  title: string;
  summary: string;
  tickets: [TicketData];
}

interface RecommendedSolutionCardProps {
  data?: RecommendedSolution;
  parentTicketId?: string;
  query?: any;
  queryVariables?: any;
  accessor?: string;
  loadingText?: any;
  collapeOnInsert?: boolean;
  onCollapseComplete?: () => void;
  mixpanelEventOnInsert?: any;
  sendReplyResponse?: any;
  setSendReplyResponse?: () => void;
  sendReplyData?: any;
  setSendReplyData?: () => void;
  onCopy?: () => void;
}

const SendReplyCard: React.FC<RecommendedSolutionCardProps> = ({
  data,
  parentTicketId,
  query,
  queryVariables,
  accessor,
  loadingText,
  collapeOnInsert,
  onCollapseComplete,
  mixpanelEventOnInsert,
  sendReplyResponse,
  setSendReplyResponse,
  sendReplyData,
  setSendReplyData,
  onCopy
}) => {
  const intl = useIntl();
  const [selectedArticle, setSelectedArticle] = useState();
  const [showKb, setShowKb] = useState(false);
  const toggleKBSheetActive = useCallback(
    () => setShowKb((prevState) => !prevState),
    []
  );
  const [collapsed, setCollapsed] = useState(false);
  const classNames = ['ai-collapsed-card', collapsed && 'collapsed']
    .filter(Boolean)
    .join(' ');

  const elementRef = useRef(null);

  const loadingTextData = [
    getTranslatedText(intl, 'ai.recommended.solutions.reply.loading.message.1'),
    getTranslatedText(intl, 'ai.recommended.solutions.reply.loading.message.2'),
    getTranslatedText(intl, 'ai.recommended.solutions.reply.loading.message.3'),
    getTranslatedText(intl, 'ai.recommended.solutions.reply.loading.message.4')
  ];

  const scrollToElement = () => {
    if (elementRef.current) {
      elementRef.current.scrollIntoView({
        behavior: 'smooth', // You can use 'auto' or 'smooth'
        block: 'start' // 'start', 'center', 'end', or 'nearest',
      });
    }
  };

  const afterProcessingMD = (htmlText: string) => {
    EventEmitterWithIndex.dispatch('insertTicketConversation', {
      content: htmlText
    });
    if (mixpanelEventOnInsert) {
      useActivityTracker(mixpanelEventOnInsert);
    }
    if (collapeOnInsert) {
      setTimeout(() => {
        // Added time out for insterted animation
        setCollapsed(true);
      }, 500);

      if (onCollapseComplete) {
        setTimeout(() => {
          // Adding timeout for shrink
          onCollapseComplete();
        }, 2000);
      }
    }
  };

  const onInsert = () => {
    const md = markdownit({
      html: true,
      linkify: true,
      typographer: true
    });

    const processMarkdownLinks = async (markdown: string) => {
      const linkRegex = /\[([^\]]+)\]\((\d+)\)/g;
      const matches = [...markdown.matchAll(linkRegex)];

      if (matches.length === 0) {
        return markdown;
      }

      let processedMarkdown = markdown;

      for (const match of matches) {
        const [fullMatch, text, id] = match;
        try {
          const MatchedArticle = sendReplyResponse?.recommendedSolutionArticles.find(
            (article: any) => article?.id === id
          );
          const url = await fetchUrlFromId(id, MatchedArticle);

          if (MatchedArticle?.loginRequired) {
            processedMarkdown = processedMarkdown.replace(
              fullMatch,
              `[${text}](${url})`
            );
          } else {
            processedMarkdown = processedMarkdown.replace(
              fullMatch,
              `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`
            );
          }
        } catch (error) {
          console.error(`Error fetching URL for ID ${id}:`, error);
        }
      }

      return processedMarkdown;
    };

    const fetchUrlFromId = async (id: string, MatchedArticle: any) => {
      if (MatchedArticle?.loginRequired) {
        return `${window.location.origin}/#/kb/article/${id}/read`;
      } else {
        const { data } = await AccountClient.mutate({
          mutation: GENERATE_SHARE_LINK,
          variables: {
            input: {
              kbArticle: {
                itemId: id
              }
            }
          }
        });
        return data?.generateKbArticleShareLink?.url;
      }
    };

    if (onCopy) {
      onCopy();
    }

    processMarkdownLinks(
      sendReplyResponse?.recommendedReply || sendReplyResponse
    )
      .then((processedMarkdown) => {
        const htmlText = md.render(processedMarkdown);
        afterProcessingMD(htmlText);
      })
      .catch((error) => {
        console.error('Error processing markdown links:', error);
        const htmlText = md.render(sendReplyResponse?.recommendedReply);
        afterProcessingMD(htmlText);
      });
  };

  const variables = queryVariables || {
    ticketId: parentTicketId,
    recommendedSolutionTicketIds: data?.tickets.map((ticket) => {
      if (ticket?.ticketId) {
        return ticket?.ticketId;
      }
    }),
    recommendedSolution: data?.summary || '',
    recommendedSolutionKBArticles: data?.kbArticles || []
  };

  const { loading: sendReplyLoading } = useQuery(
    query || GET_AI_RECOMMENDED_SOLUTION_REPLY_V2,
    {
      client: MLClient,
      fetchPolicy: 'no-cache',
      variables: variables,
      skip: !isEmpty(sendReplyData),
      onCompleted: (res) => {
        const response = accessor
          ? res?.[accessor]
          : res?.getAIRecommendedSolutionReplyV2;

        if (response) {
          if (setSendReplyResponse) {
            setSendReplyResponse(response);
          }
          const processedMarkdownAttr = response?.recommendedReply || response;
          const processedMarkdown = processedMarkdownAttr?.replace(
            /\[([^\]]+)\]\((\d+)\)\./g,
            '[$1]($2)'
          );
          if (setSendReplyData) {
            setSendReplyData(processedMarkdown);
          }
          setTimeout(() => {
            scrollToElement();
          }, 10);
        }
      }
    }
  );

  if (sendReplyLoading || isEmpty(sendReplyData)) {
    return (
      <InlineLoader
        text={loadingText || loadingTextData}
        variation="aiSubdued"
      />
    );
  }
  const openArticleSheet = (id: string) => {
    useActivityTracker({
      event: 'AI_RECOMMENDED_SOLUTION',
      data: {
        stepName: 'SEND_REPLY_ARTICLE_READ'
      }
    });
    setSelectedArticle({ id });
    toggleKBSheetActive();
  };

  const CustomLink = ({ href, children }) => {
    const isCitation = /^\d+$/.test(href);

    if (isCitation) {
      return (
        <>
          <SourceComponent id={href}>{children}</SourceComponent>
        </>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  };

  function SourceComponent({ id, children }) {
    return (
      <span
        className="cp vividBlue overflow-ellipses"
        onClick={() => openArticleSheet(id)}
      >
        {children}
      </span>
    );
  }

  return (
    <div className={classNames} id="recommended-reply" ref={elementRef}>
      <div className="flex actr fx-sb mb10">
        <div className="flex g10 actr">
          <div className="flex">
            <SVGIcon name="AI_MAIL" />
          </div>
          <div>
            <TextStyle>
              <FormattedMessage id="common.suggested.reply" />
            </TextStyle>
          </div>
        </div>

        <div>
          <AICopyAndPaste
            data={{
              copyButtonText: getTranslatedText(intl, 'common.insert.text'),
              copySuccessText: getTranslatedText(intl, 'common.inserted'),
              onCopy: onInsert
            }}
          />
        </div>
      </div>

      <div>
        <ReactMarkdown
          components={{
            a: CustomLink
          }}
        >
          {sendReplyData}
        </ReactMarkdown>
      </div>
      <Sheet open={showKb} onClose={toggleKBSheetActive}>
        <div className="w700 bg-white full-height p2040 flex fcol g20 scroll">
          <ArticleReader
            selectedArticle={selectedArticle}
            closeSheet={toggleKBSheetActive}
            hideActions
          />
        </div>
      </Sheet>
    </div>
  );
};

export default SendReplyCard;
